import { useState, useEffect, useCallback, useRef } from "react";
import inventoryApi from './inventoryApi';

/* ─────────────────────────────────────────────
   MONTH LABEL HELPER
───────────────────────────────────────────── */
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const monthLabel = (month, year) =>
  `${MONTH_NAMES[(month ?? 1) - 1]} ${year}`;

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
function Toast({ toasts, onDismiss }) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl shadow-xl border text-sm font-medium max-w-sm animate-slide-in
            ${t.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-rose-50 border-rose-200 text-rose-800"
            }`}
        >
          <span className="text-base mt-0.5">
            {t.type === "success" ? "✓" : "✕"}
          </span>
          <span className="flex-1">{t.message}</span>
          <button
            onClick={() => onDismiss(t.id)}
            className="opacity-50 hover:opacity-100 transition-opacity ml-1"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   REFILL MODAL
───────────────────────────────────────────── */
function RefillModal({ item, bag, onClose, onSuccess }) {
  const [qty, setQty] = useState(1);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!qty || qty < 1) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        inventory_bag: bag.id,
        inventory_item: item.inventory_item,
        quantity_added: Number(qty),
        remarks: remarks.trim(),
      };
      const result = await inventoryApi.createRefill(payload);
      onSuccess(result, item, qty);
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string"
          ? err.response.data
          : "Failed to add refill. Please try again.");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const itemName = item?.inventory_item_details?.item_name ?? "Item";

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-violet-200 text-xs font-semibold uppercase tracking-widest">
                Add Refill
              </p>
              <h2 className="text-white font-bold text-lg leading-tight mt-0.5">
                {itemName}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-violet-200 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg w-8 h-8 flex items-center justify-center text-lg font-light"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Current Stock Info */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="text-amber-500 text-xl">⚠</span>
            <div>
              <p className="text-amber-700 text-xs font-semibold uppercase tracking-wide">
                Current Stock
              </p>
              <p className="text-amber-900 font-bold text-lg">
                {item?.quantity_remaining ?? 0}{" "}
                <span className="text-sm font-normal text-amber-700">
                  {item?.inventory_item_details?.unit ?? "units"} remaining
                </span>
              </p>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-slate-600 text-sm font-semibold mb-2">
              Quantity to Add
              <span className="text-rose-500 ml-1">*</span>
            </label>
            <input
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition"
              placeholder="Enter quantity"
            />
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-slate-600 text-sm font-semibold mb-2">
              Remarks{" "}
              <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition resize-none"
              placeholder="e.g. Routine monthly top-up"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 border border-slate-200 text-slate-600 font-semibold rounded-xl py-2.5 hover:bg-slate-50 transition text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !qty || Number(qty) < 1}
              className="flex-1 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold rounded-xl py-2.5 hover:from-violet-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Saving…
                </>
              ) : (
                "Confirm Refill"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CATEGORY BADGE
───────────────────────────────────────────── */
function CategoryBadge({ category }) {
  if (category === "CONSUMABLE") {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 border border-violet-200">
        Consumable
      </span>
    );
  }
  if (category === "CAPITAL") {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
        Capital
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
      {category}
    </span>
  );
}

/* ─────────────────────────────────────────────
   CURRENT STOCK TAB
───────────────────────────────────────────── */
function CurrentStockTab({ bag, onRefill }) {
  const items = bag?.inventory_items ?? [];

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <span className="text-4xl mb-3">📦</span>
        <p className="font-medium">No inventory items assigned</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-100">
            {["Item", "Category", "Assigned", "Remaining", "Status", "Action"].map(
              (h) => (
                <th
                  key={h}
                  className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3"
                >
                  {h}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {items.map((inv) => {
            const detail = inv.inventory_item_details ?? {};
            const isLow = inv.is_low_stock;
            const isConsumable = detail.category === "CONSUMABLE";
            return (
              <tr
                key={inv.id}
                className={`hover:bg-slate-50/70 transition-colors ${
                  isLow ? "bg-rose-50/30" : ""
                }`}
              >
                <td className="px-4 py-3.5">
                  <span className="font-semibold text-slate-800">
                    {detail.item_name}
                  </span>
                  {detail.unit && (
                    <span className="text-slate-400 text-xs ml-1.5">
                      / {detail.unit}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  <CategoryBadge category={detail.category} />
                </td>
                <td className="px-4 py-3.5 text-slate-600 font-medium">
                  {inv.quantity_assigned}
                </td>
                <td className="px-4 py-3.5">
                  <span
                    className={`font-bold text-base ${
                      isLow ? "text-rose-600" : "text-slate-700"
                    }`}
                  >
                    {inv.quantity_remaining}
                  </span>
                </td>
                <td className="px-4 py-3.5">
                  {isLow ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                      Low Stock
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      OK
                    </span>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  {isConsumable ? (
                    <button
                      onClick={() => onRefill(inv)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold transition-colors shadow-sm"
                    >
                      + Add Refill
                    </button>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────
   REFILL HISTORY TAB
───────────────────────────────────────────── */
function RefillHistoryTab({ bagId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await inventoryApi.getRefillsByBag(bagId);
        if (!cancelled) {
          const results = Array.isArray(data)
            ? data
            : Array.isArray(data?.results)
            ? data.results
            : [];
          setLogs(results);
        }
      } catch {
        if (!cancelled) setError("Failed to load refill history.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [bagId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <svg
          className="animate-spin h-8 w-8 text-violet-400 mb-3"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <p className="text-sm">Loading history…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-rose-400">
        <span className="text-3xl mb-2">⚠</span>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <span className="text-4xl mb-3">📋</span>
        <p className="font-medium">No refills recorded yet</p>
        <p className="text-sm mt-1">Refills added for this bag will appear here.</p>
      </div>
    );
  }

  const formatDate = (str) => {
    if (!str) return "—";
    const d = new Date(str);
    if (isNaN(d)) return str;
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-100">
            {["Date", "Item", "Qty Added", "Remarks"].map((h) => (
              <th
                key={h}
                className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {logs.map((log) => (
            <tr key={log.id} className="hover:bg-slate-50/70 transition-colors">
              <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">
                {formatDate(log.created_at || log.date)}
              </td>
              <td className="px-4 py-3.5 text-slate-800 font-medium">
                {log.inventory_item_details?.item_name ||
                  log.refill_summary?.split(" ")?.[0] ||
                  "—"}
              </td>
              <td className="px-4 py-3.5">
                <span className="inline-flex items-center gap-1 font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full text-xs">
                  +{log.quantity_added}
                </span>
              </td>
              <td className="px-4 py-3.5 text-slate-500 text-sm">
                {log.remarks || (
                  <span className="text-slate-300">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────
   RIGHT PANEL
───────────────────────────────────────────── */
function RightPanel({ bag, onRefillSuccess }) {
  const [activeTab, setActiveTab] = useState("stock");
  const [selectedItem, setSelectedItem] = useState(null);
  const [bagData, setBagData] = useState(bag);

  // Sync when bag changes
  useEffect(() => {
    setBagData(bag);
    setActiveTab("stock");
  }, [bag?.id]);

  const lowCount = (bagData?.inventory_items ?? []).filter((i) => i.is_low_stock).length;

  const handleRefillSuccess = (result, item, qty) => {
    // Optimistically update both remaining and assigned quantity
    setBagData((prev) => ({
      ...prev,
      inventory_items: prev.inventory_items.map((inv) =>
        inv.id === item.id
          ? {
              ...inv,
              quantity_assigned: inv.quantity_assigned + Number(qty),
              quantity_remaining: inv.quantity_remaining + Number(qty),
              is_low_stock: false,
            }
          : inv
      ),
    }));
    onRefillSuccess(result, item, qty);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Panel Header */}
      <div className="px-6 pt-6 pb-0 border-b border-slate-100">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-0.5">
              Inventory Bag
            </p>
            <h2 className="text-xl font-bold text-slate-900">
              {bagData?.current_technician_details?.name ?? "Technician"}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
              {bagData?.bag_label
                ? `${bagData.bag_label} · `
                : ""}
              {monthLabel(bagData?.month, bagData?.year)}
            </p>
          </div>
          {lowCount > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-semibold">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
              {lowCount} Low Stock
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {[
            { key: "stock", label: "Current Stock" },
            { key: "history", label: "Refill History" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-all border-b-2 -mb-px ${
                activeTab === t.key
                  ? "text-violet-700 border-violet-600 bg-violet-50/60"
                  : "text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === "stock" ? (
          <CurrentStockTab
            bag={bagData}
            onRefill={(item) => setSelectedItem(item)}
          />
        ) : (
          <RefillHistoryTab bagId={bagData.id} />
        )}
      </div>

      {/* Refill Modal */}
      {selectedItem && (
        <RefillModal
          item={selectedItem}
          bag={bagData}
          onClose={() => setSelectedItem(null)}
          onSuccess={handleRefillSuccess}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   TECHNICIAN CARD
───────────────────────────────────────────── */
function TechnicianCard({ bag, selected, onClick }) {
  const lowCount = (bag.inventory_items ?? []).filter((i) => i.is_low_stock).length;
  const techName = bag.current_technician_details?.name ?? "Unknown";

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all duration-150 group ${
        selected
          ? "bg-violet-600 border-violet-600 shadow-lg shadow-violet-200"
          : "bg-white border-slate-200 hover:border-violet-300 hover:shadow-md"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p
            className={`font-semibold text-sm truncate ${
              selected ? "text-white" : "text-slate-800"
            }`}
          >
            {techName}
          </p>
          <p
            className={`text-xs mt-0.5 ${
              selected ? "text-violet-200" : "text-slate-400"
            }`}
          >
            {bag.bag_label ? `${bag.bag_label} · ` : ""}
            {monthLabel(bag.month, bag.year)}
          </p>
        </div>
        {lowCount > 0 ? (
          <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-rose-500 text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" />
            {lowCount}
          </span>
        ) : (
          <span
            className={`shrink-0 w-2 h-2 rounded-full ${
              selected ? "bg-violet-300" : "bg-emerald-400"
            }`}
          />
        )}
      </div>
    </button>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
export default function RefillManagement() {
  const [bags, setBags] = useState([]);
  const [bagsLoading, setBagsLoading] = useState(true);
  const [bagsError, setBagsError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedBag, setSelectedBag] = useState(null);
  const [toasts, setToasts] = useState([]);
  const toastId = useRef(0);

  /* ── Fetch bags ── */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setBagsLoading(true);
      setBagsError(null);
      try {
        const data = await inventoryApi.getBags();
        if (!cancelled) {
          const results = Array.isArray(data)
            ? data
            : Array.isArray(data?.results)
            ? data.results
            : [];
          setBags(results);
        }
      } catch {
        if (!cancelled) setBagsError("Failed to load technician bags.");
      } finally {
        if (!cancelled) setBagsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  /* ── Toast helpers ── */
  const addToast = useCallback((message, type = "success") => {
    const id = ++toastId.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /* ── Refill success callback ── */
  const handleRefillSuccess = useCallback(
    (result, item, qty) => {
      const name = item?.inventory_item_details?.item_name ?? "Item";
      addToast(`Refill added successfully! ${name} +${qty} units`);
    },
    [addToast]
  );

  /* ── Filtered bags ── */
  const filteredBags = bags.filter((b) => {
    const name = b.current_technician_details?.name ?? "";
    return name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <>
      {/* Inject animation keyframes */}
      <style>{`
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-in { animation: slide-in 0.2s ease-out forwards; }
      `}</style>

      <Toast toasts={toasts} onDismiss={dismissToast} />

      <div className="min-h-screen bg-slate-50 font-sans">
        {/* Top bar */}
        <div className="bg-white border-b border-slate-100 px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white text-sm font-bold shadow">
            R
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-900 leading-tight">
              Refill Management
            </h1>
            <p className="text-xs text-slate-400">
              Coordinator · Active technician inventory
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col md:flex-row h-[calc(100vh-64px)]">
          {/* ── LEFT PANEL ── */}
          <aside className="w-full md:w-80 lg:w-96 shrink-0 bg-white border-b md:border-b-0 md:border-r border-slate-100 flex flex-col">
            {/* Panel header */}
            <div className="px-5 pt-5 pb-4 border-b border-slate-100">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                Select Technician
              </p>
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search technician…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition bg-slate-50 placeholder-slate-400"
                />
              </div>
            </div>

            {/* Bag list */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {bagsLoading && (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <svg
                    className="animate-spin h-7 w-7 text-violet-400 mb-3"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p className="text-sm">Loading technicians…</p>
                </div>
              )}

              {!bagsLoading && bagsError && (
                <div className="flex flex-col items-center justify-center py-12 text-rose-400 px-4 text-center">
                  <span className="text-3xl mb-2">⚠</span>
                  <p className="text-sm">{bagsError}</p>
                </div>
              )}

              {!bagsLoading && !bagsError && filteredBags.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400 px-4 text-center">
                  <span className="text-3xl mb-2">🔍</span>
                  <p className="text-sm font-medium">No technicians found</p>
                  {search && (
                    <p className="text-xs mt-1">
                      Try a different search term
                    </p>
                  )}
                </div>
              )}

              {!bagsLoading &&
                !bagsError &&
                filteredBags.map((bag) => (
                  <TechnicianCard
                    key={bag.id}
                    bag={bag}
                    selected={selectedBag?.id === bag.id}
                    onClick={() => setSelectedBag(bag)}
                  />
                ))}
            </div>

            {/* Footer count */}
            {!bagsLoading && !bagsError && (
              <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-400 font-medium">
                {filteredBags.length} bag{filteredBags.length !== 1 ? "s" : ""} shown
                {bags.filter((b) => (b.inventory_items ?? []).some((i) => i.is_low_stock)).length > 0 && (
                  <span className="ml-2 text-rose-500">
                    ·{" "}
                    {bags.filter((b) =>
                      (b.inventory_items ?? []).some((i) => i.is_low_stock)
                    ).length}{" "}
                    need refill
                  </span>
                )}
              </div>
            )}
          </aside>

          {/* ── RIGHT PANEL ── */}
          <main className="flex-1 overflow-hidden flex flex-col bg-white">
            {selectedBag ? (
              <RightPanel
                key={selectedBag.id}
                bag={selectedBag}
                onRefillSuccess={handleRefillSuccess}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 px-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-3xl mb-4">
                  👤
                </div>
                <p className="font-semibold text-slate-600 text-lg">
                  Select a technician
                </p>
                <p className="text-sm mt-1 max-w-xs">
                  Choose a technician from the list to view their inventory and add refills.
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
}